import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, type Article } from '@/mocks/articles';

const articleCategoryImages: Record<string, string> = {
  'register-guide-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=register-guide-01&orientation=landscape',
  'features-intro-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=features-intro-01&orientation=landscape',
  'update-news-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=update-news-01&orientation=landscape',
  'faq-common-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=faq-common-01&orientation=landscape',
  'security-guide-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=security-guide-01&orientation=landscape',
  'pro-tips-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=pro-tips-01&orientation=landscape',
  'mobile-guide-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=mobile-guide-01&orientation=landscape',
  'data-analysis-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20article%20illustration%20with%20soft%20gradient%20background%2C%20warm%20coral%20and%20cream%20tones%2C%20abstract%20geometric%20elements%2C%20clean%20editorial%20style%2C%20subtle%20texture%2C%20professional%20blog%20featured%20image&width=800&height=400&seq=data-analysis-01&orientation=landscape',
};

function getArticlesByCategory(slug: string): Article[] {
  return articles.filter((a) => a.category === slug);
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  const activeCategoryData = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  const handleCategoryClick = (catSlug: string) => {
    setActiveCategory(catSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.pushState(null, '', `/category/${catSlug}`);
  };

  const pageTitle = activeCategoryData ? `${activeCategoryData.name} - 17c.cc` : '内容分类';
  const pageDescription = activeCategoryData
    ? activeCategoryData.description
    : '浏览 17c.cc 各分类内容，获取使用指南、功能介绍及常见问题解答';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    isPartOf: {
      '@type': 'WebSite',
      name: '17c.cc',
      url: 'https://17c.cc',
    },
  };

  return (
    <>
      <SEOHead
        title={activeCategoryData?.name || '内容分类'}
        description={pageDescription}
        keywords={`17c.cc,17c.cc${activeCategoryData?.name || ''},17c.cc使用指南,17c.cc功能介绍`}
        canonicalUrl={`https://17c.cc/category/${activeCategory}`}
        structuredData={structuredData}
      />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategoryData?.name || '内容分类' },
          ]}
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                内容分类
              </h1>
              <p className="text-foreground-500 text-base max-w-2xl mx-auto">
                浏览 17c.cc 各分类内容，找到您需要的文章和教程
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {activeCategoryData && (
              <div className="mb-10 p-8 rounded-xl bg-accent-50/50 border border-accent-200/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                    <i className={`${activeCategoryData.icon} text-xl`}></i>
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-900">
                    {activeCategoryData.name}
                  </h2>
                </div>
                <p className="text-sm text-foreground-600">
                  {activeCategoryData.description}
                </p>
              </div>
            )}

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={articleCategoryImages[article.imageSeq] || articleCategoryImages['register-guide-01']}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}